import { css } from '@emotion/react';
import { type ReactNode, memo, useMemo } from 'react';
import { useVirtualGrid } from '../../../hooks/useVirtualScroll';

interface VirtualGridProps<T> {
  items: T[];
  itemHeight: number;
  height: number;
  columnCount: number;
  gap?: number;
  renderItem: (item: T, index: number) => ReactNode;
  emptyComponent?: ReactNode;
}

function VirtualGridInner<T>({
  items,
  itemHeight,
  height,
  columnCount,
  gap = 0,
  renderItem,
  emptyComponent,
}: VirtualGridProps<T>) {
  const { startRow, endRow, totalHeight, rowHeight, handleScroll } = useVirtualGrid({
    itemCount: items.length,
    itemHeight,
    containerHeight: height,
    columnCount,
    gap,
  });

  const containerCss = useMemo(() => containerStyle(height), [height]);
  const innerCss = useMemo(() => innerStyle(totalHeight), [totalHeight]);

  // 보여줄 아이템 목록 계산
  const visibleItems = useMemo(() => {
    const result: { item: T; index: number; row: number; col: number }[] = [];
    for (let row = startRow; row <= endRow; row++) {
      for (let col = 0; col < columnCount; col++) {
        const index = row * columnCount + col;
        if (index >= 0 && index < items.length) {
          result.push({ item: items[index], index, row, col });
        }
      }
    }
    return result;
  }, [startRow, endRow, columnCount, items]);

  // 그리드 아이템 스타일 계산 (메모이제이션)
  const getItemStyle = useMemo(() => {
    const colWidth = `calc((100% - ${gap * (columnCount - 1)}px) / ${columnCount})`;
    return (row: number, col: number) => ({
      position: 'absolute' as const,
      top: row * rowHeight,
      height: itemHeight,
      width: colWidth,
      left: `calc(${col} * (${colWidth} + ${gap}px))`,
    });
  }, [gap, columnCount, rowHeight, itemHeight]);

  if (items.length === 0) return <>{emptyComponent}</>;

  return (
    <div css={containerCss} onScroll={handleScroll}>
      <div css={innerCss}>
        {visibleItems.map(({ item, index, row, col }) => (
          <div key={index} style={getItemStyle(row, col)}>
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
}

export const VirtualGrid = memo(VirtualGridInner) as typeof VirtualGridInner;

const containerStyle = (h: number) => css`
  height: ${h}px;
  overflow-y: auto;
  position: relative;
`;

const innerStyle = (h: number) => css`
  height: ${h}px;
  position: relative;
`;
